import type { ResponsiveValue } from '@chakra-ui/react';
import React, { forwardRef, useMemo } from 'react';

import {
  HStack,
  VStack,
  type BoxProps,
  type TextProps,
} from '@frontend/components';

import { GDTSymbolText } from './GDTSymbolText';
import type {
  ToleranceDistribution,
  StatisticalParameters,
  ToleranceFrame,
  GDTControlFrameProps,
  GDTIconProps,
  GDTModifier,
} from './types';

// Constants for GDT symbols and modifiers
const GDT_MODIFIERS = {
  PROJECTED: 'Ⓟ',
  UNEQUAL: 'Ⓤ',
  STATISTICAL: 'Ⓢ',
} as const;

const GDT_SYMBOLS = {
  DIAMETER: '⌀',
  SPHERICAL_DIAMETER: 'S⌀',
} as const;

// CSS class names for better maintainability
const CSS_CLASSES = {
  SINGLE: 'gdt-cf__single',
  CHARACTERISTIC: 'gdt-cf__characteristic',
  TOLERANCE: 'gdt-cf__tolerance',
  MODIFIERS: 'gdt-cf__modifiers',
  DATUM_REFERENCE: 'gdt-cf__datum-reference',
  DATUM: 'gdt-cf__datum',
  DATUM_MODIFIERS: 'gdt-cf__datum-modifiers',
  PROJECTED_HEIGHT: 'gdt-cf__projected-height',
  STATISTICAL: 'gdt-cf__statistical',
  STATISTICAL_PARAM: 'gdt-cf__statistical-param',
  COMPOSITE: 'gdt-cf gdt-cf--composite',
  COMPOSITE_CONTENT: 'gdt-cf__composite-content',
  PATTERN: 'gdt-cf__pattern',
  FEATURE: 'gdt-cf__feature',
  STACKED: 'gdt-cf gdt-cf--stacked',
  SINGLE_FRAME: 'gdt-cf gdt-cf--single',
} as const;

// Style applied to every GDTSymbolText contained in a control frame
const sectionStyle = {
  border: '1px solid',
  px: '0.2em',
  alignSelf: 'stretch',
  placeItems: 'center',
  placeContent: 'center',
  sx: {
    '&:not(:last-child)': {
      marginInlineEnd: '-1px',
    },
  },
} as const;

/**
 * Helper function to build effective modifiers array
 * @internal
 */
const buildEffectiveModifiers = (
  modifiers: GDTModifier[],
  showProjected: boolean,
  showUnequal: boolean,
  showStatistical: boolean,
): GDTModifier[] => {
  const effectiveModifiers = [...modifiers];

  if (
    showProjected &&
    !effectiveModifiers.includes(GDT_MODIFIERS.PROJECTED as GDTModifier)
  ) {
    effectiveModifiers.push(GDT_MODIFIERS.PROJECTED as GDTModifier);
  }
  if (
    showUnequal &&
    !effectiveModifiers.includes(GDT_MODIFIERS.UNEQUAL as GDTModifier)
  ) {
    effectiveModifiers.push(GDT_MODIFIERS.UNEQUAL as GDTModifier);
  }
  if (
    showStatistical &&
    !effectiveModifiers.includes(GDT_MODIFIERS.STATISTICAL as GDTModifier)
  ) {
    effectiveModifiers.push(GDT_MODIFIERS.STATISTICAL as GDTModifier);
  }

  return effectiveModifiers;
};

/**
 * Helper function to render tolerance value with proper formatting
 * @internal
 */
const renderToleranceValue = (
  tolerance: string,
  unit: string | undefined,
  showUnequal: boolean,
  toleranceDistribution: ToleranceDistribution | undefined,
  iconProps: GDTIconProps,
  size: ResponsiveValue<string>,
): React.ReactNode => {
  if (showUnequal && toleranceDistribution) {
    return (
      <>
        <GDTSymbolText iconProps={iconProps} size={size}>
          {`+${toleranceDistribution.plus}`}
        </GDTSymbolText>
        <GDTSymbolText iconProps={iconProps} size={size}>
          {`-${toleranceDistribution.minus}`}
        </GDTSymbolText>
        {unit && (
          <GDTSymbolText iconProps={iconProps} size={size}>
            {unit}
          </GDTSymbolText>
        )}
      </>
    );
  }

  return (
    <GDTSymbolText iconProps={iconProps} size={size}>
      {`${tolerance}${unit || ''}`}
    </GDTSymbolText>
  );
};

/**
 * Helper function to render statistical parameters
 * @internal
 */
const renderStatisticalParameters = (
  statisticalParameters: StatisticalParameters,
  iconProps: GDTIconProps,
  size: ResponsiveValue<string>,
): React.ReactNode => {
  const params = [];

  if (statisticalParameters.processCapability) {
    params.push(
      <GDTSymbolText
        key="cpk"
        iconProps={iconProps}
        size={size}
        className={CSS_CLASSES.STATISTICAL_PARAM}
      >
        {`Cpk=${statisticalParameters.processCapability}`}
      </GDTSymbolText>,
    );
  }

  if (statisticalParameters.sampleSize) {
    params.push(
      <GDTSymbolText
        key="sample-size"
        iconProps={iconProps}
        size={size}
        className={CSS_CLASSES.STATISTICAL_PARAM}
      >
        {`n=${statisticalParameters.sampleSize}`}
      </GDTSymbolText>,
    );
  }

  if (statisticalParameters.confidenceLevel) {
    params.push(
      <GDTSymbolText
        key="confidence-level"
        iconProps={iconProps}
        size={size}
        className={CSS_CLASSES.STATISTICAL_PARAM}
      >
        {`CL=${statisticalParameters.confidenceLevel}`}
      </GDTSymbolText>,
    );
  }

  return params;
};

/**
 * Component for rendering a single control frame
 * @internal
 */
const SingleControlFrame = React.memo(
  forwardRef<
    HTMLDivElement,
    ToleranceFrame & {
      iconProps: GDTIconProps;
      showCharacteristic?: boolean;
    } & BoxProps &
      TextProps
  >(
    (
      {
        characteristic,
        tolerance,
        unit,
        modifiers = [],
        toleranceZoneShape = 'none',
        datumReferences = [],
        projectedHeight,
        toleranceDistribution,
        statisticalParameters,
        iconProps,
        size = 'md',
        showCharacteristic = true,
        ...boxProps
      },
      ref,
    ) => {
      // Memoize computed values to avoid recalculation on every render
      const computedValues = useMemo(() => {
        const showDiameter = toleranceZoneShape === 'diameter';
        const showSpherical = toleranceZoneShape === 'spherical';
        const showProjected = projectedHeight !== undefined;
        const showUnequal = toleranceDistribution?.type === 'unequal';
        const showStatistical = statisticalParameters?.type === 'statistical';

        // Build effective modifiers array
        const effectiveModifiers = buildEffectiveModifiers(
          modifiers,
          showProjected,
          showUnequal,
          showStatistical,
        );

        return {
          showDiameter,
          showSpherical,
          showProjected,
          showUnequal,
          showStatistical,
          effectiveModifiers,
        };
      }, [
        toleranceZoneShape,
        projectedHeight,
        toleranceDistribution?.type,
        statisticalParameters?.type,
        modifiers,
      ]);

      return (
        <HStack
          spacing={0}
          alignItems="center"
          ref={ref}
          marginInlineEnd="-1px"
          marginBlockEnd="-1px"
          className={CSS_CLASSES.SINGLE}
          {...boxProps}
        >
          {/* Characteristic symbol */}
          {showCharacteristic && characteristic && (
            <GDTSymbolText
              iconProps={iconProps}
              size={size}
              className={CSS_CLASSES.CHARACTERISTIC}
              {...sectionStyle}
            >
              {characteristic}
            </GDTSymbolText>
          )}

          {/* Tolerance value and unit */}
          <HStack
            spacing={0}
            className={CSS_CLASSES.TOLERANCE}
            {...sectionStyle}
          >
            {computedValues.showDiameter && (
              <GDTSymbolText iconProps={iconProps} size={size}>
                {GDT_SYMBOLS.DIAMETER}
              </GDTSymbolText>
            )}
            {computedValues.showSpherical && (
              <GDTSymbolText iconProps={iconProps} size={size}>
                {GDT_SYMBOLS.SPHERICAL_DIAMETER}
              </GDTSymbolText>
            )}
            {computedValues.showUnequal && toleranceDistribution ? (
              renderToleranceValue(
                tolerance,
                unit,
                computedValues.showUnequal,
                toleranceDistribution,
                iconProps,
                size,
              )
            ) : (
              <GDTSymbolText iconProps={iconProps} size={size}>
                {`${tolerance}${unit || ''}`}
              </GDTSymbolText>
            )}

            {/* Modifiers */}
            {computedValues.effectiveModifiers.length > 0 && (
              <GDTSymbolText
                iconProps={iconProps}
                size={size}
                className={CSS_CLASSES.MODIFIERS}
              >
                {computedValues.effectiveModifiers.join('')}
              </GDTSymbolText>
            )}
          </HStack>

          {/* Datum references */}
          {datumReferences.length > 0 && (
            <React.Fragment>
              {datumReferences.map((ref) => (
                <HStack
                  key={ref.datum}
                  className={CSS_CLASSES.DATUM_REFERENCE}
                  spacing={0}
                  {...sectionStyle}
                >
                  <GDTSymbolText
                    iconProps={iconProps}
                    size={size}
                    className={CSS_CLASSES.DATUM}
                    {...(ref.modifiers && ref.modifiers.length > 0
                      ? {}
                      : {
                          minWidth: '1.25em',
                          textAlign: 'center',
                        })}
                  >
                    {ref.datum}
                  </GDTSymbolText>
                  {ref.modifiers && ref.modifiers.length > 0 && (
                    <GDTSymbolText
                      iconProps={iconProps}
                      size={size}
                      className={CSS_CLASSES.DATUM_MODIFIERS}
                    >
                      {ref.modifiers.join('')}
                    </GDTSymbolText>
                  )}
                </HStack>
              ))}
            </React.Fragment>
          )}

          {/* Projected height */}
          {computedValues.showProjected && projectedHeight && (
            <GDTSymbolText
              iconProps={iconProps}
              size={size}
              className={CSS_CLASSES.PROJECTED_HEIGHT}
              {...sectionStyle}
            >
              {projectedHeight}
            </GDTSymbolText>
          )}

          {/* Statistical parameters */}
          {computedValues.showStatistical && statisticalParameters && (
            <HStack
              spacing={0}
              className={CSS_CLASSES.STATISTICAL}
              {...sectionStyle}
            >
              {renderStatisticalParameters(
                statisticalParameters,
                iconProps,
                size,
              )}
            </HStack>
          )}
        </HStack>
      );
    },
  ),
);

SingleControlFrame.displayName = 'SingleControlFrame';

/**
 * GDTControlFrame component for rendering Geometric Dimensioning and Tolerancing control frames.
 * This component supports all standard GDT characteristics, modifiers, and special cases including:
 * - Form controls (⏥, ⏤, ○, ⌭)
 * - Orientation controls (⊥, ∥, ∠)
 * - Location controls (⌖, ◎, ⌴)
 * - Runout controls (↗, ⌰)
 * - Profile controls (⌒, ⌓)
 * - Composite tolerances
 * - Projected tolerance zones
 * - Statistical tolerances
 * - Unequally disposed tolerances
 */
export const GDTControlFrame = React.memo(
  forwardRef<HTMLDivElement, GDTControlFrameProps>(
    (
      {
        characteristic,
        tolerance,
        unit,
        modifiers = [],
        toleranceZoneShape = 'none',
        datumReferences = [],
        projectedHeight,
        toleranceDistribution,
        statisticalParameters,
        patternTolerance,
        featureTolerance,
        iconProps = {},
        size = 'md',
        isComposite = false,
        ...textProps
      },
      ref,
    ) => {
      // If we have composite tolerances, render them in an HStack
      if (patternTolerance && featureTolerance && isComposite) {
        return (
          <HStack
            spacing={0}
            alignItems="stretch"
            ref={ref}
            className={CSS_CLASSES.COMPOSITE}
            marginBottom="1px"
          >
            <GDTSymbolText
              className={CSS_CLASSES.CHARACTERISTIC}
              {...sectionStyle}
              marginBlockEnd="-1px"
            >
              {characteristic || patternTolerance.characteristic || ''}
            </GDTSymbolText>
            <VStack
              spacing={0}
              align="stretch"
              className={CSS_CLASSES.COMPOSITE_CONTENT}
            >
              <SingleControlFrame
                {...patternTolerance}
                iconProps={iconProps}
                size={size}
                className={CSS_CLASSES.PATTERN}
                showCharacteristic={false}
                {...textProps}
              />
              <SingleControlFrame
                {...featureTolerance}
                iconProps={iconProps}
                size={size}
                className={CSS_CLASSES.FEATURE}
                showCharacteristic={false}
                {...textProps}
              />
            </VStack>
          </HStack>
        );
      }

      // If we have multiple tolerances but not composite, render them stacked
      if (patternTolerance && featureTolerance) {
        return (
          <VStack
            spacing={0}
            alignItems="start"
            marginBlockEnd="1px"
            ref={ref}
            className={CSS_CLASSES.STACKED}
          >
            <SingleControlFrame
              {...patternTolerance}
              iconProps={iconProps}
              size={size}
              className={CSS_CLASSES.PATTERN}
              {...textProps}
            />
            <SingleControlFrame
              {...featureTolerance}
              iconProps={iconProps}
              size={size}
              className={CSS_CLASSES.FEATURE}
              {...textProps}
            />
          </VStack>
        );
      }

      // Otherwise render a single control frame
      if (!characteristic || !tolerance) {
        return null;
      }
      return (
        <SingleControlFrame
          ref={ref}
          characteristic={characteristic}
          tolerance={tolerance}
          unit={unit}
          modifiers={modifiers}
          toleranceZoneShape={toleranceZoneShape}
          datumReferences={datumReferences}
          projectedHeight={projectedHeight}
          toleranceDistribution={toleranceDistribution}
          statisticalParameters={statisticalParameters}
          iconProps={iconProps}
          size={size}
          marginBlockEnd="1px"
          className={CSS_CLASSES.SINGLE_FRAME}
          {...textProps}
        />
      );
    },
  ),
);

GDTControlFrame.displayName = 'GDTControlFrame';
